import { motion, AnimatePresence } from 'motion/react';
import {
  MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { IconPropsType } from '../../util/types';
import { makeClassName } from '@siteless/util';
import Loader from '../Loader';

let Button = ({
  onClick,
  disabled = false,
  children,
  Icon,
  AcknowledgeClickIcon,
  className,
  stacked = false,
  isLoading = false,
}: {
  Icon?: (props: IconPropsType) => ReactElement;
  AcknowledgeClickIcon?: (props: IconPropsType) => ReactElement;
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
  stacked?: boolean;
  isLoading?: boolean;
}) => {
  let [clickTrip, setClickTrip] = useState(false);

  let handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      if (disabled) return;
      onClick(event);
      setClickTrip(true);
    },
    [onClick, disabled, setClickTrip],
  );

  useEffect(() => {
    if (!clickTrip) return;

    let timeout = setTimeout(() => {
      setClickTrip(false);
    }, 1800);

    return () => {
      clearTimeout(timeout);
    };
  }, [clickTrip]);


  return (
    <button
      className={makeClassName(
        'Button text-md pointer relative inline-flex items-center overflow-hidden rounded-xl border-4 font-medium uppercase',
        className,
        'text-neutral-0 border-rose-dark bg-rose-default',
        stacked && 'mb-3 last:mb-0',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
      onClick={handleClick}
      disabled={disabled}
    >
      {isLoading ? (
        children ? (
          <div className="bg-rose-default text-neutral-0 absolute inset-0 flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div
            className="absolute inset-0 animate-pulse"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.15) 100%)',
            }}
          />
        )
      ) : null}
      {Icon && (
        <div className="relative mr-3 flex-none last:mr-0">
          {AcknowledgeClickIcon ? (
            <>
              <AnimatePresence mode="wait" initial={false}>
                {clickTrip ? (
                  <motion.div
                    key="acknowledge"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 200, damping: 15 } }}
                    exit={{ opacity: 0, scale: 0, transition: { duration: 0.15 } }}
                    className="absolute"
                  >
                    <AcknowledgeClickIcon />
                  </motion.div>
                ) : (
                  <motion.div
                    key="default"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 200, damping: 15 } }}
                    exit={{ opacity: 0, scale: 0, transition: { duration: 0.15 } }}
                    className="absolute"
                  >
                    <Icon />
                  </motion.div>
                )}
              </AnimatePresence>
              <Icon className="invisible" />
            </>
          ) : (
            <Icon />
          )}
        </div>
      )}
      {Boolean(children) && <span className="flex-1">{children}</span>}
    </button>
  );
};

export default Button;
